import { ReorderLessonDocument, type ReorderLessonMutation } from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import type { LessonFields } from "@/types/Lesson";
import { useMutation } from "@apollo/client/react";

export function useReorderLesson() {
  const [reorderLessonMutation, { loading }] = useMutation(ReorderLessonDocument);

  const reorderLesson = (
    lesson: LessonFields,
    parentId: string | null,
    prevSortOrder: number | null,
    newSortOrder: number | null
  ) => {
    return withToast(
      async () => {
        const optimisticSortOrder = newSortOrder ?? (prevSortOrder ?? 0) + 1000;

        return reorderLessonMutation({
          variables: {
            lessonId: lesson.id,
            parentId,
            prevSortOrder,
            newSortOrder,
          },
          // The server may renumber siblings too, but only the moved lesson can be
          // predicted here; the real response replaces this layer with the full set.
          optimisticResponse: {
            reorderLesson: [
              {
                __typename: "Lesson",
                ...lesson,
                sortOrder: optimisticSortOrder,
              } as ReorderLessonMutation["reorderLesson"][number],
            ],
          },
        });
      },
      {
        loading: "Updating...",
        success: "Lesson Order Updated!",
        error: "Failed to update lesson order. Please try again.",
      },
      "reorderLessonToast"
    );
  };

  return { reorderLesson, loading };
}
